import { createRouter, createWebHistory } from 'vue-router';
import { useSeoMeta } from '@unhead/vue';
import { authState, login } from '@lcp/http-auth';
import { SPARK_DEFAULT_OG_IMAGE_URL, SPARK_SITE_URL } from '@/constants/SparkSite.ts';

import { isDynamicImportError, handleDynamicImportError, getPendingRoute } from '@/utils/dynamicImportErrorHandler.ts';
import { routes } from './routes/routes.ts';
import { useCentralModalStore } from './stores/centralModalStore.ts';

const router = createRouter({
    history: createWebHistory(),
    routes,
});

/**
 * Handle dynamic import failures (typically caused by deployments)
 * This catches errors when chunk files are no longer available after deployment
 */
router.onError((error, to) => {
    if (isDynamicImportError(error) && to?.fullPath) {
        handleDynamicImportError(to.fullPath);
    } else {
        // Re-throw other errors
        throw error;
    }
});

router.beforeEach(async () => {
    const centralModalStore = useCentralModalStore();
    if (!(await centralModalStore.shouldAllowNavigation())) return false;
    centralModalStore.closeAllModals();
    return true;
});

router.beforeResolve(async (to, _from, next) => {
    // Handle pending route after page reload due to dynamic import failure
    const pendingRoute = getPendingRoute();
    if (pendingRoute && pendingRoute !== to.fullPath) {
        next(pendingRoute);
        return;
    }

    if (to.path === '/') {
        next({ name: 'Home' });
        return;
    }

    if (to.meta.requiresLogin && !authState.value.loggedIn) {
        login();
        next(false);
        return;
    }

    if (to.meta.name === undefined && (to.name === undefined || to.name === null)) {
        next({ name: '404' });
        return;
    }

    next();
});

router.afterEach(async (to) => {
    // A lot of this is also duplictaed in index.html, so any changes need replicating.
    const title = `Spark ${to.meta.headName ? `- ${to.meta.headName}` : ''}`;
    const description = 'Your gateway to the Energy Transition. Spark smarter decisions by fusing LCP Delta\'s deep research, data analytics, and powerful forecasting.';

    useSeoMeta({
        title,
        ogTitle: title,
        twitterTitle: title,

        description,
        ogDescription: description,
        twitterDescription: description,

        ogUrl: `${SPARK_SITE_URL}${router.currentRoute.value.fullPath}`,

        ogImage: {
            url: SPARK_DEFAULT_OG_IMAGE_URL,
            width: 1200,
            height: 630,
            alt: title,
            type: 'image/png',
        },
        twitterImage: {
            url: SPARK_DEFAULT_OG_IMAGE_URL,
            width: 1200,
            height: 630,
            alt: title,
            type: 'image/png',
        },

        twitterCard: 'summary',
    });
});

export {
    router,
};
